import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, Signal, WritableSignal, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ActivityLogClickDirective } from '@client-ui/directives/activity-log-click.directive';
import { paths } from '@platform/paths';
import { IconComponent } from '@shared-lib/components/icon/icon.component';
import { ANALYTIC_CLICK_EVENT_NAMES } from '@shared/constants/analytic-click-event-names';
import { ButtonDirective } from 'primeng/button';
import { InputMaskModule } from 'primeng/inputmask';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Observable, interval } from 'rxjs';
import { debounceTime, filter, take, withLatestFrom } from 'rxjs/operators';
import { AuthFacadeService } from '../../auth-facade.service';
import { SignInCodeErrorComponent } from '../../components/sign-in-code-error/sign-in-code-error.component';
import { verificationCodeValidator } from './verification-code.validator';

@Component({
  selector: 'cp-login-verification-code',
  templateUrl: './login-verification-code.component.html',
  styleUrls: ['../../auth-theme.scss', 'login-verification-code.component.scss'],
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    ButtonDirective,
    IconComponent,
    RouterLink,
    InputMaskModule,
    ActivityLogClickDirective,
    ProgressSpinner,
    SignInCodeErrorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginVerificationCodeComponent implements OnInit {
  private readonly facade = inject(AuthFacadeService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly paths = paths;
  protected readonly AnalyticClickEventNames = ANALYTIC_CLICK_EVENT_NAMES;

  value = null;

  lastTimeVerificationCodeSent$: Observable<Date> = this.facade.lastTimeVerificationCodeSent$;
  authError$: Observable<string> = this.facade.authError$;
  isIncorrectCodeError$: Observable<boolean> = this.facade.isIncorrectCodeError$;
  isNewCodeSent$: Observable<boolean> = this.facade.isNewCodeSent$;
  lastSignInCode$: Observable<string> = this.facade.lastSignInCode$;
  isLoadingSignInCompanies$: Observable<boolean> = this.facade.isLoadingSignInCompanies$;

  protected readonly isSendingVerificationCode: Signal<boolean> = toSignal(this.facade.isSendingVerificationCode$, { initialValue: false });
  protected readonly isSendingVerificationCodeSlowly: Signal<boolean> = toSignal(
    this.facade.isSendingVerificationCode$.pipe(debounceTime(3000)),
    { initialValue: false },
  );

  protected readonly timeLeftTillResendEnabled: WritableSignal<number> = signal(30);

  verificationCodeForm: FormGroup<{ verificationCode: FormControl<string> }>;
  email: string;

  ngOnInit(): void {
    this.verificationCodeForm = new FormGroup({
      verificationCode: new FormControl<string>(null, [verificationCodeValidator]),
    });

    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.timeLeftTillResendEnabled.update((timeLeft) => Math.max(0, timeLeft - 1)));

    this.lastSignInCode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((code: string) => {
      this.verificationCodeForm.controls.verificationCode.setValue(code, { emitEvent: false });
    });

    this.lastTimeVerificationCodeSent$.pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.timeLeftTillResendEnabled.set(30);
    });

    this.isLoadingSignInCompanies$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isLoadingSignInCompanies) => {
      if (isLoadingSignInCompanies) {
        this.verificationCodeForm.disable({ emitEvent: false });
      } else {
        this.verificationCodeForm.enable({ emitEvent: false });
      }
    });

    this.facade.email$.pipe(take(1)).subscribe((email) => {
      if (!email) {
        this.facade.navigateToCheckMail();
      } else {
        this.email = email;
      }
    });

    this.verificationCodeForm.valueChanges
      .pipe(withLatestFrom(this.authError$, this.isNewCodeSent$), takeUntilDestroyed(this.destroyRef))
      .subscribe(([, authError, isNewCodeSent]) => {
        if (authError) {
          this.facade.clearAuthError();
        }
        if (isNewCodeSent) {
          this.facade.hideNewCodeSent();
        }
        if (this.verificationCodeForm.valid) {
          this.checkManualLoginCode();
        }
      });
  }

  protected resendCode(): void {
    if (this.timeLeftTillResendEnabled() === 0 && !this.isSendingVerificationCode()) {
      this.facade.resendVerificationCode(this.email);
    }
  }

  private checkManualLoginCode(): void {
    if (this.verificationCodeForm.valid) {
      this.facade.checkManualLoginCode(this.verificationCodeForm.value.verificationCode);
    }
  }
}
